import React, { useEffect, useState } from 'react'
import { Alert, Button, Spinner } from 'flowbite-react'
import { HiExclamation, HiPencil, HiPlus, HiTrash } from 'react-icons/hi'
import toast from 'react-hot-toast'
import { toastApiError } from '../utils/apiError'
import {
  buildCreateBody,
  buildUpdateBody,
  createAor,
  dedupeKeywords,
  deleteAor,
  listAors,
  reorderAors,
  updateAor,
} from '../utils/departments'
import EntityFormModal from './EntityFormModal'
import KeywordEditor from './KeywordEditor'
import { ReorderButtons, StatusBadges, swap } from './EntityRowControls'

/**
 * Areas of responsibility for a single department. Always loads active AND
 * inactive rows so the reorder id list stays complete; `showInactive` only
 * controls what is displayed and whether reordering is offered.
 */
const AorList = ({
  token,
  authorityId,
  departmentId,
  canWrite,
  showInactive,
  knownKeywords = [],
  duplicateKeys,
  onAorsChange,
}) => {
  const [aors, setAors] = useState([])
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState(null)
  const [reordering, setReordering] = useState(false)
  const [modal, setModal] = useState({ open: false, mode: 'create', entity: null })

  const load = async () => {
    setLoading(true)
    try {
      const data = await listAors(token, authorityId, departmentId, true)
      setAors(Array.isArray(data) ? data : [])
    } catch (error) {
      toastApiError(error, 'Failed to load areas of responsibility')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, authorityId, departmentId])

  // Report this department's AoRs up so the authority-wide keyword counts (used
  // for duplicate highlighting) reflect live edits. Skip the initial empty state.
  useEffect(() => {
    if (loading) return
    onAorsChange?.(departmentId, aors)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aors, loading, departmentId])

  const clearSiblingCatchAll = (list, keepId) =>
    list.map((item) =>
      item.id === keepId ? item : item.is_catch_all ? { ...item, is_catch_all: false } : item
    )

  const handleCreate = async (form) => {
    const created = await createAor(token, authorityId, departmentId, buildCreateBody(form))
    setAors((current) => {
      const next = [...current, created]
      return created.is_catch_all ? clearSiblingCatchAll(next, created.id) : next
    })
    setModal({ open: false, mode: 'create', entity: null })
    toast.success('Area of responsibility created')
  }

  const handleEdit = async (form) => {
    const original = modal.entity
    const body = buildUpdateBody(form, original)
    if (Object.keys(body).length === 0) {
      setModal({ open: false, mode: 'edit', entity: null })
      return
    }
    const updated = await updateAor(token, authorityId, departmentId, original.id, body)
    setAors((current) => {
      const next = current.map((item) => (item.id === updated.id ? updated : item))
      return updated.is_catch_all ? clearSiblingCatchAll(next, updated.id) : next
    })
    setModal({ open: false, mode: 'edit', entity: null })
    toast.success('Area of responsibility updated')
  }

  const handleToggleActive = async (aor) => {
    if (aor.active && aor.is_catch_all) {
      toast('Heads up: this is the catch-all for the department.', { icon: '⚠️' })
    }
    setBusyId(aor.id)
    try {
      const updated = await updateAor(token, authorityId, departmentId, aor.id, {
        active: !aor.active,
      })
      setAors((current) => current.map((item) => (item.id === updated.id ? updated : item)))
    } catch (error) {
      toastApiError(error, 'Failed to update area of responsibility')
    } finally {
      setBusyId(null)
    }
  }

  const handleDelete = async (aor) => {
    const warning = aor.is_catch_all
      ? '\n\nThis is the catch-all — unmatched reports in this department will have nowhere to route until you set another.'
      : ''
    if (
      !window.confirm(
        `Delete area of responsibility "${aor.name}"? This cannot be undone.${warning}`
      )
    ) {
      return
    }
    setBusyId(aor.id)
    try {
      await deleteAor(token, authorityId, departmentId, aor.id)
      setAors((current) => current.filter((item) => item.id !== aor.id))
      toast.success('Area of responsibility deleted')
    } catch (error) {
      toastApiError(error, 'Failed to delete area of responsibility')
    } finally {
      setBusyId(null)
    }
  }

  const handleKeywordsChange = async (aor, nextKeywords) => {
    const keywords = dedupeKeywords(nextKeywords)
    const previous = aors
    // Optimistic: reflect the new pills immediately, roll back on failure.
    setAors((current) =>
      current.map((item) => (item.id === aor.id ? { ...item, keywords } : item))
    )
    setBusyId(aor.id)
    try {
      const updated = await updateAor(token, authorityId, departmentId, aor.id, {
        keywords,
      })
      setAors((current) => current.map((item) => (item.id === updated.id ? updated : item)))
    } catch (error) {
      setAors(previous)
      toastApiError(error, 'Failed to update issue keywords')
    } finally {
      setBusyId(null)
    }
  }

  const handleMove = async (index, direction) => {
    const reordered = swap(aors, index, direction)
    if (reordered === aors) return
    const previous = aors
    setAors(reordered)
    setReordering(true)
    try {
      const data = await reorderAors(
        token,
        authorityId,
        departmentId,
        reordered.map((item) => item.id)
      )
      setAors(Array.isArray(data) ? data : reordered)
    } catch (error) {
      setAors(previous)
      if (error?.response?.status === 400) {
        toast.error('Order was out of sync — reloading the latest list.')
        load()
      } else {
        toastApiError(error, 'Failed to reorder areas of responsibility')
      }
    } finally {
      setReordering(false)
    }
  }

  const visible = showInactive ? aors : aors.filter((item) => item.active)
  const canReorder = canWrite && showInactive
  const activeAors = aors.filter((item) => item.active)
  const missingCatchAll = activeAors.length > 0 && !activeAors.some((item) => item.is_catch_all)

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-3 text-sm text-gray-500 dark:text-gray-400">
        <Spinner size="sm" /> Loading areas of responsibility…
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Areas of responsibility
        </h4>
        {canWrite && (
          <Button
            size="xs"
            color="gray"
            onClick={() => setModal({ open: true, mode: 'create', entity: null })}
          >
            <HiPlus className="mr-1 h-3.5 w-3.5" />
            Add AoR
          </Button>
        )}
      </div>

      {canWrite && missingCatchAll && (
        <Alert color="warning" icon={HiExclamation}>
          No active catch-all AoR — reports that match no specific area in this department
          won&apos;t route.
        </Alert>
      )}

      {visible.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No areas of responsibility{showInactive ? '' : ' active'} yet.
        </p>
      ) : (
        <ul className="space-y-2">
          {visible.map((aor) => {
            const fullIndex = aors.findIndex((item) => item.id === aor.id)
            return (
              <li
                key={aor.id}
                className={`rounded-lg border border-gray-200 bg-gray-50/60 p-3 dark:border-gray-700 dark:bg-gray-800/40 ${
                  aor.active ? '' : 'opacity-60'
                }`}
              >
                <div className="flex items-start gap-3">
                  {canReorder && (
                    <div className="pt-0.5">
                      <ReorderButtons
                        label={aor.name}
                        isFirst={fullIndex === 0}
                        isLast={fullIndex === aors.length - 1}
                        disabled={reordering}
                        onUp={() => handleMove(fullIndex, -1)}
                        onDown={() => handleMove(fullIndex, 1)}
                      />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate text-sm font-medium text-gray-900 dark:text-white">
                        {aor.name}
                      </span>
                      <StatusBadges entity={aor} />
                    </div>
                    {aor.description && (
                      <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                        {aor.description}
                      </p>
                    )}
                  </div>
                  {canWrite && (
                    <div className="flex shrink-0 items-center gap-1">
                      <Button
                        size="xs"
                        color="gray"
                        disabled={busyId === aor.id}
                        onClick={() => handleToggleActive(aor)}
                      >
                        {aor.active ? 'Deactivate' : 'Activate'}
                      </Button>
                      <Button
                        size="xs"
                        color="gray"
                        disabled={busyId === aor.id}
                        onClick={() => setModal({ open: true, mode: 'edit', entity: aor })}
                      >
                        <HiPencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="xs"
                        color="failure"
                        disabled={busyId === aor.id}
                        onClick={() => handleDelete(aor)}
                      >
                        <HiTrash className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  )}
                </div>

                <div className="mt-3 border-t border-gray-200 pt-3 dark:border-gray-700">
                  <KeywordEditor
                    keywords={aor.keywords ?? []}
                    knownKeywords={knownKeywords}
                    duplicateKeys={duplicateKeys}
                    canWrite={canWrite}
                    disabled={busyId === aor.id}
                    onChange={(next) => handleKeywordsChange(aor, next)}
                  />
                </div>
              </li>
            )
          })}
        </ul>
      )}

      <EntityFormModal
        open={modal.open}
        mode={modal.mode}
        entityLabel="Area of Responsibility"
        catchAllHint="The catch-all AoR receives reports routed to this department that match no specific area. Only one per department — setting this clears it on the current catch-all."
        initial={modal.mode === 'edit' ? modal.entity : null}
        onClose={() => setModal({ open: false, mode: 'create', entity: null })}
        onSubmit={modal.mode === 'edit' ? handleEdit : handleCreate}
      />
    </div>
  )
}

export default AorList
